import { use } from "react";
import { RecordsContext } from "@domain/context/accountingData/RecordsCtx";
import { BuContext } from "@domain/context/BuContext";
import type { GenericRecord, Payment } from "@domain/models";
import { GenericRecordTableItemCalculator, TableUtils } from "@domain/core/TableUtils";
import { PaymentsContext } from "@domain/context/accountingData/PaymentsProvider";
import { GQL, Model } from "@binale-tech/shared";
import { type ICCD } from "@domain/models/Interfaces";
import { useSaldoSources } from "@domain/context/accountingData/SaldoSourcesProvider";

export const useSaldoTransferInput = (
    yearTo: number,
    modules: Set<GQL.IProductKey>,
    companyData: Pick<GQL.ICompany, "accountingConfigs" | "accountingYears" | "id">,
    shouldSkip = false,
) => {
    const payments = use(PaymentsContext);
    const { recordsBank, recordsKB, recordsER, recordsAZ, recordsDeb, recordsFE, allRecords } = use(RecordsContext);
    const { map: saldoSourcesMap } = useSaldoSources();
    const { companyBuTimeframes } = use(BuContext);

    if (!companyData) {
        return null;
    }
    if (shouldSkip) {
        return null;
    }

    const accountingYears = companyData.accountingYears || [];
    const yearFrom = yearTo - 1;
    const accountingConfig = companyData.accountingConfigs.find((v) => v.year === yearFrom);

    if (!accountingConfig) {
        return null;
    }

    const getGroupSaldo = (recordsList: GenericRecord[]): number => {
        // yearConfigs doesn't impact saldo calculation, so we take the first one
        const calculator = new GenericRecordTableItemCalculator(recordsList, (records) =>
            TableUtils.getTableItems(records, payments, companyBuTimeframes, accountingConfig),
        );
        const saldoMap = calculator.getSaldoMap(accountingYears);
        if (saldoMap.has(yearFrom)) {
            return saldoMap.get(yearFrom).get(Model.Period.LastDayOfYear) ?? 0;
        }
        return 0;
    };
    const getKbs = (): GQL.ICompanyTransferSaldoGroupInput[] => {
        const res: GQL.ICompanyTransferSaldoGroupInput[] = [];
        if (!modules.has(GQL.IProductKey.Kb)) {
            return res;
        }
        for (const [groupId, { list: recordsList }] of recordsKB.groups) {
            const saldo = getGroupSaldo(recordsList);
            if (saldo !== 0) {
                res.push({ groupId, saldo });
            }
        }
        return res;
    };
    const getBanks = (): GQL.ICompanyTransferSaldoGroupInput[] => {
        const res: GQL.ICompanyTransferSaldoGroupInput[] = [];
        if (!modules.has(GQL.IProductKey.Bank)) {
            return res;
        }
        for (const [groupId, { list: recordsList }] of recordsBank.groups) {
            const saldo = getGroupSaldo(recordsList);
            if (saldo !== 0) {
                res.push({ groupId, saldo });
            }
        }
        return res;
    };
    const filterRecord = (record: GenericRecord) => {
        if (record.year !== yearFrom) {
            return false;
        }
        if (record.draft) {
            return false;
        }
        if (record.cancellation) {
            return false;
        }
        return true;
    };
    const getRecordPayments = (recordId: string) => {
        const list = payments.recordRelation.get(recordId) ?? [];
        return list.filter((payment) => payment.date.getFullYear() < yearTo);
    };
    const getOpenPaymentRecordIds = (
        recordList: GenericRecord[],
        pk: GQL.IProductKey,
    ): GQL.ICompanyTransferSaldoPaymentsInput => {
        const sourceIds: { sourceId: string; saldoSourceId: string; connectedPayments: Payment[] }[] = [];
        recordList = recordList.filter(filterRecord);
        for (const record of recordList) {
            const saldoOriginRecord = record.saldoSourceId
                ? (allRecords.map.get(record.saldoSourceId) ?? saldoSourcesMap.get(record.saldoSourceId))
                : record;
            if (!saldoOriginRecord) {
                continue;
            }
            const connectedPayments = getRecordPayments(saldoOriginRecord.key);
            const openAmount = saldoOriginRecord.getOpenBrutto(connectedPayments);
            // if (record.key === "-Oeb9_4BW4oyqQwETx5R") {
            //     console.log("found", openAmount, saldoOriginRecord, connectedPayments);
            // }
            if (openAmount === 0) {
                continue;
            }
            sourceIds.push({
                sourceId: record.key,
                connectedPayments,
                saldoSourceId: record.saldoSourceId || undefined,
            });
        }

        const bankIds: GQL.IRecordIdIndexInput[] = [];
        const kbIds: GQL.IRecordIdIndexInput[] = [];
        const feIds: GQL.IRecordIdIndexInput[] = [];

        const processedIds = new Set<string>();
        sourceIds.forEach(({ sourceId, connectedPayments }) => {
            connectedPayments.forEach((payment) => {
                processedIds.add(payment.representationRecordKey);
                processedIds.add(payment.autoFEDiscountRecordKey);
                // processedIds.add(payment.autoFEBalancingAZRecordKey);
            });
        });
        const isDebitor = pk === GQL.IProductKey.Deb;
        const filterDisconnected = (record: GenericRecord) => {
            if (!filterRecord(record)) {
                return false;
            }
            if (processedIds.has(record.key)) {
                return false;
            }

            const hasPaymentSourceUpToThisYear = (recordId: string) => {
                const [connectedPayment] = payments.recordRelation.get(recordId) ?? [];
                if (!connectedPayment) {
                    return false;
                }
                const paymentSourceRecord =
                    allRecords.map.get(connectedPayment.sourceRecordKey) ??
                    saldoSourcesMap.get(connectedPayment.sourceRecordKey);
                if (!paymentSourceRecord) {
                    return false;
                }
                return paymentSourceRecord.year <= yearFrom;
                // return Boolean(connectedPayment);
            };

            if (hasPaymentSourceUpToThisYear(record.key)) {
                // console.log(record.key, "DENY: has payment source by own id");
                return false;
            }
            if (record.saldoSourceId && hasPaymentSourceUpToThisYear(record.saldoSourceId)) {
                // console.log(record.key, "DENY: has payment source by saldo source id");
                return false;
            }
            // console.log(record.key, "ALLOWED");
            return true;
        };
        const addToAccumulator = (
            entity: ICCD,
            accumulator: GQL.IRecordIdIndexInput[],
            recordId: string,
            itemIndex: number,
        ) => {
            if (isDebitor) {
                if (entity.debetor) {
                    accumulator.push({ recordId, itemIndex });
                }
            } else {
                if (entity.creditor) {
                    accumulator.push({ recordId, itemIndex });
                }
            }
        };
        const processPaymentRepresentationRecords = (
            records: GenericRecord[],
            accumulator: GQL.IRecordIdIndexInput[],
        ) => {
            records = records.filter(filterDisconnected);
            for (const record of records) {
                record.items.forEach((item, index) => {
                    addToAccumulator(item, accumulator, record.key, index);
                });
            }
        };
        processPaymentRepresentationRecords(recordsBank.combinedList, bankIds);
        processPaymentRepresentationRecords(recordsKB.combinedList, kbIds);
        recordsFE.list.filter(filterDisconnected).forEach((record) => {
            if (record.debetor || record.creditor) {
                if (record.items.length > 1) {
                    // console.log("FE record has more than one item", record);
                    return;
                    // throw new Error("FE record has more than one item");
                }
                const itemCategory = record.items[0].category?.getExtNum(0) ?? "";
                // FIBU-831 filter out manually created saldo transfer records for FE items
                if (["9008", "9009"].includes(itemCategory)) {
                    return;
                }
                if (Number(itemCategory) >= 9050 && Number(itemCategory) <= 9089) {
                    return;
                }

                addToAccumulator(record, feIds, record.key, 0);
            }
        });

        return { sourceIds: sourceIds.map((v) => v.sourceId), bankIds, kbIds, feIds };
    };
    const getER = (): GQL.ICompanyTransferSaldoPaymentsInput => {
        if (!modules.has(GQL.IProductKey.Er)) {
            return null;
        }
        return getOpenPaymentRecordIds(recordsER.list, GQL.IProductKey.Er);
    };
    const getErA = (): GQL.ICompanyTransferSaldoPaymentsInput => {
        if (!modules.has(GQL.IProductKey.ErA)) {
            return null;
        }
        return getOpenPaymentRecordIds(recordsAZ.list, GQL.IProductKey.ErA);
    };
    const getDeb = (): GQL.ICompanyTransferSaldoPaymentsInput => {
        if (!modules.has(GQL.IProductKey.Deb)) {
            return null;
        }
        return getOpenPaymentRecordIds(recordsDeb.list, GQL.IProductKey.Deb);
    };
    const input: GQL.ICompanyTransferSaldoInput = {
        yearTo,
        companyId: companyData?.id,
        kb: getKbs(),
        bank: getBanks(),
        er: getER(),
        erAnz: getErA(),
        deb: getDeb(),
    };
    return input;
};
